"use client"

import Image, { ImageProps } from "next/image"
import { useMemo } from "react"

import { convertHttpToHttps, isSecureUrl } from "@/app/utils/secureUrl"

interface SecureImageProps extends Omit<ImageProps, "src"> {
  src: string
  /** Fallback image URL if src is invalid or empty */
  fallbackSrc?: string
  /** Whether to enforce HTTPS (default: true) */
  enforceHttps?: boolean
}

/**
 * SecureImage - HTTP 요청을 방어하는 Next.js Image 확장 컴포넌트
 *
 * Features:
 * - HTTP URLs를 자동으로 HTTPS로 변환
 * - 빈 URL이나 유효하지 않은 URL 처리
 * - 폴백 이미지 지원
 *
 * @example
 * ```tsx
 * <SecureImage
 *   src="http://example.com/image.jpg"
 *   alt="Example"
 *   width={100}
 *   height={100}
 *   fallbackSrc="/images/placeholder.png"
 * />
 * ```
 */
const SecureImage = ({
  src,
  fallbackSrc = "/images/illustrations/default_source.webp",
  enforceHttps = true,
  ...props
}: SecureImageProps) => {
  const secureSrc = useMemo(() => {
    const normalizedSrc = src?.trim()
    if (!normalizedSrc) {
      return fallbackSrc
    }

    if (isSecureUrl(normalizedSrc)) {
      return normalizedSrc
    }

    if (!enforceHttps) {
      return normalizedSrc
    }

    const upgraded = convertHttpToHttps(normalizedSrc)
    if (upgraded && upgraded !== normalizedSrc && isSecureUrl(upgraded)) {
      console.warn(
        `[SecureImage] HTTP URL detected and converted to HTTPS: ${normalizedSrc} -> ${upgraded}`,
      )
      return upgraded
    }

    console.error(`[SecureImage] Blocked insecure image URL: ${normalizedSrc}`)
    return fallbackSrc
  }, [src, fallbackSrc, enforceHttps])

  // eslint-disable-next-line jsx-a11y/alt-text
  return <Image {...props} src={secureSrc} />
}

export default SecureImage
export type { SecureImageProps }
